import { cn } from "@/lib/cn";

/**
 * Initials for an app with no icon of its own. Splits on any non-alphanumeric
 * run so "org-user-sync" reads as OU rather than O.
 */
function initials(title: string): string {
  const words = title.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return (words[0] ?? "").slice(0, 2).toUpperCase();
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase();
}

interface Props {
  title: string;
  iconPath: string | null;
  size?: number;
  className?: string;
}

/**
 * Icons are vendored into /app-icons at build time rather than hot-linked to
 * each app's origin: the grid must not depend on twelve other deployments being
 * up, and painting it should not fire a request at every app in the estate.
 *
 * Apps with no icon get a monogram tile that is *deliberately* plain — a dashed
 * border and muted fill, so it reads as "this app has no icon yet" rather than
 * as an icon that failed to load.
 */
export function AppIcon({ title, iconPath, size = 44, className }: Props) {
  if (iconPath) {
    return (
      <img
        src={iconPath}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        className={cn("shrink-0 rounded-[22%] object-cover", className)}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      title="No icon yet"
      className={cn(
        "bg-muted text-muted-foreground border-border grid shrink-0 place-items-center",
        "rounded-[22%] border border-dashed font-semibold",
        className,
      )}
      style={{ width: size, height: size, fontSize: size * 0.34 }}
    >
      {initials(title)}
    </span>
  );
}
